// API service for backend communication
import { API_CONFIG, API_ENDPOINTS } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface BookingRequest {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  package: string;
  date: string;
  time: string;
  location: string;
  details: string;
}

export interface BookingResponse {
  id: string;
  status: 'pending_approval' | 'approved' | 'confirmed' | 'rejected' | 'cancelled';
  createdAt: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectionReason?: string;
}

class ApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const raw = await response.json();

      // Normalize backend envelope: { success, data, error, message }
      if (raw && typeof raw === 'object' && ('success' in raw || 'data' in raw || 'error' in raw || 'message' in raw)) {
        const backendSuccess = typeof raw.success === 'boolean' ? raw.success : true;
        const normalizedData = (raw.data !== undefined ? raw.data : raw) as T;
        const backendError = typeof raw.error === 'string' ? raw.error : undefined;
        const backendMessage = typeof raw.message === 'string' ? raw.message : undefined;

        return {
          success: backendSuccess,
          data: normalizedData,
          error: backendError,
          message: backendMessage,
        };
      }

      // Fallback: raw payload is the data
      return {
        success: true,
        data: raw as T,
      };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async createBooking(bookingData: BookingRequest): Promise<ApiResponse<BookingResponse>> {
    return this.makeRequest<BookingResponse>(API_ENDPOINTS.BOOKINGS, {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async getBooking(bookingId: string): Promise<ApiResponse<BookingResponse>> {
    return this.makeRequest<BookingResponse>(API_ENDPOINTS.BOOKING_BY_ID(bookingId));
  }

  async updateBookingStatus(
    bookingId: string,
    status: 'pending_approval' | 'approved' | 'confirmed' | 'rejected' | 'cancelled'
  ): Promise<ApiResponse<BookingResponse>> {
    return this.makeRequest<BookingResponse>(API_ENDPOINTS.BOOKING_STATUS(bookingId), {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async approveBooking(
    bookingId: string,
    approvedBy: string
  ): Promise<ApiResponse<BookingResponse>> {
    return this.makeRequest<BookingResponse>(API_ENDPOINTS.BOOKING_APPROVE(bookingId), {
      method: 'POST',
      body: JSON.stringify({ approvedBy }),
    });
  }

  async rejectBooking(
    bookingId: string,
    rejectionReason: string,
    rejectedBy: string
  ): Promise<ApiResponse<BookingResponse>> {
    return this.makeRequest<BookingResponse>(API_ENDPOINTS.BOOKING_REJECT(bookingId), {
      method: 'POST',
      body: JSON.stringify({ rejectionReason, rejectedBy }),
    });
  }

  async confirmBooking(bookingId: string): Promise<ApiResponse<BookingResponse>> {
    return this.makeRequest<BookingResponse>(API_ENDPOINTS.BOOKING_CONFIRM(bookingId), {
      method: 'POST',
    });
  }

  async getAllBookings(): Promise<ApiResponse<BookingResponse[]>> {
    return this.makeRequest<BookingResponse[]>(API_ENDPOINTS.BOOKINGS);
  }
}

export const apiService = new ApiService();
